import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import { getLedgerSafeCellState } from './ledgerSafeRowEditing'
import type { LedgerSafeEditSuggestion } from './ledgerSafeEditSuggestionTypes'
import {
  asLedgerSafeSuggestionString,
  asLedgerSafeSuggestionStringArray,
  findLedgerSafeAdjacentRowForPlacement,
  ledgerSafeSuggestionRowHasActiveMapping
} from './ledgerSafeEditSuggestionRows'

export const normalizeLedgerSafeSuggestionRowIds = (rowIds: string[]): string =>
  rowIds.filter(Boolean).join('|')

export const buildLedgerSafeAppliedOperationKeySet = (events: LedgerSafeEditorialEvent[]): Set<string> => {
  const keys = new Set<string>()
  events.forEach(event => {
    const kind = event.operation?.kind
    const params = event.operation?.params || {}
    const witnessId = asLedgerSafeSuggestionString(params.witnessId || event.affectedWitnessIds?.[0])
    if (!kind || !witnessId) {
      return
    }
    switch (kind) {
      case 'boundary_shift': {
        const leftRowId = asLedgerSafeSuggestionString(params.leftRowId)
        const deltaWords = Number(params.deltaWords)
        if (leftRowId && Number.isFinite(deltaWords)) {
          keys.add(`${kind}:${witnessId}:${leftRowId}:${deltaWords}`)
        }
        break
      }
      case 'split_unit': {
        const rowId = asLedgerSafeSuggestionString(params.rowId)
        const afterWordIndex = Number(params.afterWordIndex)
        if (rowId && Number.isFinite(afterWordIndex)) {
          keys.add(`${kind}:${witnessId}:${rowId}:${afterWordIndex}`)
        }
        break
      }
      case 'merge_units':
      case 'shift_range': {
        const rowIds = asLedgerSafeSuggestionStringArray(params.rowIds)
        const direction = asLedgerSafeSuggestionString(params.direction)
        if (rowIds.length > 0) {
          keys.add(`${kind}:${witnessId}:${normalizeLedgerSafeSuggestionRowIds(rowIds)}:${direction}`)
        }
        break
      }
      case 'move_mapping':
      case 'insert_row_and_move':
      case 'mark_gap':
      case 'clear_gap': {
        const rowId = asLedgerSafeSuggestionString(params.rowId)
        const direction = asLedgerSafeSuggestionString(params.direction || params.placement)
        if (rowId) {
          keys.add(`${kind}:${witnessId}:${rowId}:${direction}`)
        }
        break
      }
      default:
        break
    }
  })
  return keys
}

export const getRecentLedgerSafeEditorialEvents = (
  editorialEvents: LedgerSafeEditorialEvent[],
  maxSourceEvents: number
): LedgerSafeEditorialEvent[] =>
  editorialEvents
    .map((event, index) => {
      const timestamp = Date.parse(event.createdAt || '')
      return {
        event,
        index,
        timestamp: Number.isFinite(timestamp) ? timestamp : index
      }
    })
    .sort((left, right) => {
      if (right.timestamp !== left.timestamp) {
        return right.timestamp - left.timestamp
      }
      return right.index - left.index
    })
    .slice(0, maxSourceEvents)
    .map(item => item.event)

export const ledgerSafeOperationKeyForSuggestion = (
  suggestion: Pick<LedgerSafeEditSuggestion, 'kind' | 'targetWitnessId' | 'rowIds' | 'params'>
): string => {
  switch (suggestion.kind) {
    case 'boundary_shift':
      return `${suggestion.kind}:${suggestion.targetWitnessId}:${asLedgerSafeSuggestionString(suggestion.params.leftRowId)}:${Number(suggestion.params.deltaWords)}`
    case 'split_unit':
      return `${suggestion.kind}:${suggestion.targetWitnessId}:${asLedgerSafeSuggestionString(suggestion.params.rowId)}:${Number(suggestion.params.afterWordIndex)}`
    case 'merge_units':
    case 'shift_range':
      return `${suggestion.kind}:${suggestion.targetWitnessId}:${normalizeLedgerSafeSuggestionRowIds(asLedgerSafeSuggestionStringArray(suggestion.params.rowIds))}:${asLedgerSafeSuggestionString(suggestion.params.direction)}`
    case 'move_mapping':
    case 'insert_row_and_move':
    case 'mark_gap':
    case 'clear_gap':
      return `${suggestion.kind}:${suggestion.targetWitnessId}:${asLedgerSafeSuggestionString(suggestion.params.rowId)}:${asLedgerSafeSuggestionString(suggestion.params.direction || suggestion.params.placement)}`
    default:
      return `${suggestion.kind}:${suggestion.targetWitnessId}:${normalizeLedgerSafeSuggestionRowIds(suggestion.rowIds)}`
  }
}

export const shouldSkipLedgerSafeSuggestion = ({
  suggestion,
  dismissedIds,
  appliedKeys
}: {
  suggestion: LedgerSafeEditSuggestion
  dismissedIds: Set<string>
  appliedKeys: Set<string>
}): boolean =>
  dismissedIds.has(suggestion.id) || appliedKeys.has(ledgerSafeOperationKeyForSuggestion(suggestion))

export const canSuggestLedgerSafeMappedRows = ({
  rowsById,
  rowIds,
  witnessId
}: {
  rowsById: Map<string, AnchoredRow>
  rowIds: string[]
  witnessId: string
}): boolean =>
  rowIds.length > 0 && rowIds.every(rowId => {
    const row = rowsById.get(rowId)
    return row && getLedgerSafeCellState(row, witnessId) === 'mapped'
  })

export const priorLedgerSafeSuggestionInsertedSlot = ({
  event,
  editorialEvents,
  rowId,
  placement,
  candidateRowId
}: {
  event: LedgerSafeEditorialEvent
  editorialEvents: LedgerSafeEditorialEvent[]
  rowId: string
  placement: 'above' | 'below'
  candidateRowId: string
}): boolean =>
  editorialEvents.some(candidateEvent => {
    const params = candidateEvent.operation?.params || {}
    return (
      candidateEvent.operation?.kind === 'insert_row_and_move' &&
      asLedgerSafeSuggestionString(candidateEvent.metadata?.suggestedFromEventId) === event.id &&
      asLedgerSafeSuggestionString(params.rowId) === rowId &&
      asLedgerSafeSuggestionString(params.placement) === placement &&
      candidateEvent.affectedRowIds.includes(candidateRowId)
    )
  })

export const findReusableLedgerSafeInsertionSlotRowId = ({
  rows,
  editorialEvents,
  event,
  sourceWitnessId,
  rowId,
  placement
}: {
  rows: AnchoredRow[]
  editorialEvents: LedgerSafeEditorialEvent[]
  event: LedgerSafeEditorialEvent
  sourceWitnessId: string
  rowId: string
  placement: 'above' | 'below'
}): string | null => {
  const anchorRow = rows.find(row => row.id === rowId)
  const candidateRow = findLedgerSafeAdjacentRowForPlacement(rows, rowId, placement)
  if (!anchorRow || !candidateRow) {
    return null
  }

  if (
    sourceWitnessId &&
    getLedgerSafeCellState(candidateRow, sourceWitnessId) === 'mapped' &&
    getLedgerSafeCellState(anchorRow, sourceWitnessId) !== 'mapped'
  ) {
    return candidateRow.id
  }

  if (
    ledgerSafeSuggestionRowHasActiveMapping(candidateRow) &&
    priorLedgerSafeSuggestionInsertedSlot({
      event,
      editorialEvents,
      rowId,
      placement,
      candidateRowId: candidateRow.id
    })
  ) {
    return candidateRow.id
  }

  return null
}
